/**
 * RootErrorFallback — full-screen error card for throws in non-admin routes.
 *
 * Used as defaultErrorComponent on createRouter. Covers: login, root index, and
 * any throw that escapes _admin. Renders without admin chrome (AppHeader is
 * mounted inside the _admin route, not at root level).
 *
 * Logs to console.error on mount — consistent with RichTextErrorBoundary.tsx:24.
 */

import { Button } from '@repo/ui/components/button';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { BilingualErrorCard } from './BilingualErrorCard';

export function RootErrorFallback({ error, info, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    console.error('[RootErrorFallback]', error, info?.componentStack);
  }, [error, info]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-8">
      <BilingualErrorCard
        titleBn="অপ্রত্যাশিত সমস্যা"
        titleEn="UNEXPECTED ERROR"
        bodyBn="পেজ রেন্ডার করার সময় কিছু একটা ভেঙে গেছে। আপনার ডেটা হারায়নি — আবার চেষ্টা করুন।"
        bodyEn="Something broke while rendering this page. Your data is not lost — reload to try again. If the problem repeats, copy the error message below and report it."
        errorMessage={error?.message}
        actions={
          <div className="flex gap-3">
            <Button
              onClick={reset}
              className="bg-red text-red-ink hover:bg-red/90 min-h-[44px] px-6"
            >
              আবার চেষ্টা / RETRY
            </Button>
            <Button
              variant="outline"
              onClick={() => router.navigate({ to: '/' })}
              className="min-h-[44px] px-6"
            >
              হোম / HOME
            </Button>
          </div>
        }
      />
    </div>
  );
}
